import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import { User, UserInput } from '@core/generated/gql-types';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import EditableSection from '@/components/shared/EditableSection';
import CvProfileDisplay from '@/components/shared/CvProfileDisplay';
import {
  actionFieldInputBlockClassName,
  actionPrimaryButtonClassName,
} from '@core/theme/actionTheme.util';
import {
  CANDIDATE_SUMMARY_AI_DISCLAIMER,
  CANDIDATE_SUMMARY_MAX_LENGTH,
  getWorkerPriorityEngagementPoints,
  getWorkerCvSummary,
  isWorkerCvSummaryAiGenerated,
} from '@/modules/workers/utils/cvProfile.utils';

interface CandidateSummaryProps {
  worker: User;
}

const CandidateSummary: React.FC<CandidateSummaryProps> = ({ worker }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { updateWorker, fetchDetailedWorkerById } = useWorkerStore();

  const initialValues = {
    summary: getWorkerCvSummary(worker),
  };
  const isAiGenerated = isWorkerCvSummaryAiGenerated(worker);

  const handleSave = async (values: typeof initialValues) => {
    const trimmedSummary = values.summary.trim();
    if (trimmedSummary.length > CANDIDATE_SUMMARY_MAX_LENGTH) {
      toast.error(
        `Candidate summary must be ${CANDIDATE_SUMMARY_MAX_LENGTH} characters or fewer`,
        {
          position: 'bottom-left',
          autoClose: 5000,
        },
      );
      return;
    }

    setIsSaving(true);
    try {
      const userInput: UserInput = {
        cvProfile: {
          summary: trimmedSummary,
          priorityEngagementPoints: getWorkerPriorityEngagementPoints(worker),
        },
      };

      await updateWorker(worker.id, userInput);
      await fetchDetailedWorkerById(worker.id);
      setIsEditing(false);
      toast.success('Candidate summary updated', {
        position: 'bottom-left',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error updating candidate summary:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update candidate summary',
        {
          position: 'bottom-left',
          autoClose: 5000,
        },
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditableSection
      title="Candidate Summary"
      isEditing={isEditing}
      onEdit={() => setIsEditing(true)}
      onCancel={() => setIsEditing(false)}
      showSaveButton={false}
      collapsible
    >
      {!isEditing ? (
        <div className="w-1/2">
          <CvProfileDisplay worker={worker} showAiDisclaimer />
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ values, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="w-1/2">
                <Field
                  as="textarea"
                  id="candidate-summary"
                  name="summary"
                  rows={5}
                  maxLength={CANDIDATE_SUMMARY_MAX_LENGTH}
                  disabled={isSaving}
                  className={`${actionFieldInputBlockClassName} resize-y`}
                />
                <div className="mt-1 flex items-center justify-between gap-3">
                  {isAiGenerated ? (
                    <p className="text-xs text-gray-500 italic">
                      {CANDIDATE_SUMMARY_AI_DISCLAIMER}
                    </p>
                  ) : null}
                  <p className="ml-auto shrink-0 text-xs text-gray-500">
                    {values.summary.length} / {CANDIDATE_SUMMARY_MAX_LENGTH}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex w-1/2 flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </EditableSection>
  );
};

export default CandidateSummary;
